import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { clipboardIcon, dataJsonIcon } from "@progress/kendo-svg-icons";
import { Eye, EyeOff } from "lucide-react";
import { Fragment, useCallback, useState } from "react";
import { SovraSkeleton } from "../../../../components/skeleton/sovraSkeleton";
import type { CommercialPlanData, PlanByMediaTypeItem, PlanByPeriodItem, PlanByRetailerItem } from "../../../../models/commericalPlanModels";
import { calculateExpectedVelocity } from "../../../../utils/commercialPlanCalculations";
import { formatCurrency, formatPercentage, isDefinedNumber } from "../../../../utils/numberValueConversion";
import { isAdministrator } from "../../../../utils/userRoleUtils";
import CommercialPlanInsights from "./commercialPlanInsights";
import DataConfidence from "./dataConfidence";
import DataQuality from "./dataQuality";

type CommercialPlanGridProps = {
    planData?: CommercialPlanData | null;
};

type CommercialPlanTableProps = CommercialPlanGridProps & {
    planByRetailer: PlanByRetailerItem[];
    handleActionButtonClick: () => void;
};

type PeriodMetrics = {
    totalVelocityChange: number | null;
    expectedVelocity: number | null;
    totalRetailMediaSpend: number | null;
    totalTradeSpend: number | null;
    expectedRevenue: number | null;
    expectedProfit: number | null;
};

const calculateTotalRetailMediaSpend = (planByMediaType: PlanByMediaTypeItem[] | null | undefined): number | null => {
    if (!planByMediaType || planByMediaType.length === 0) {
        return 0;
    }

    if (!planByMediaType.every(media => isDefinedNumber(media.media_type_spend))) {
        return null;
    }

    return planByMediaType.reduce<number>((total, media) => {
        if (!isDefinedNumber(media.media_type_spend)) {
            return total;
        }

        return total + media.media_type_spend;
    }, 0);
};

const calculateTotalVelocityChange = (period: PlanByPeriodItem): number | null => {
    if (
        !isDefinedNumber(period.promotion_velocity_change)
        || !isDefinedNumber(period.period_compounded_velocity_change)
    ) {
        return null;
    }

    const mediaVelocityChanges = period.plan_by_media_type.map(
        (media: PlanByMediaTypeItem): number | null =>
            media.media_type_velocity_change
    );

    if (!mediaVelocityChanges.every(isDefinedNumber)) {
        return null;
    }

    const totalMediaVelocityChange = mediaVelocityChanges.reduce<number>(
        (total: number, velocityChange: number): number =>
            total + velocityChange,
        0
    );

    return period.promotion_velocity_change
        + totalMediaVelocityChange
        + period.period_compounded_velocity_change;
};

const calculateExpectedRevenue = (
    period: PlanByPeriodItem,
    expectedVelocity: number | null
): number | null => {
    if (!isDefinedNumber(period.promotion_price) || !isDefinedNumber(expectedVelocity)) {
        return null;
    }

    return period.promotion_price * expectedVelocity;
};

const calculateExpectedProfit = (
    period: PlanByPeriodItem,
    expectedVelocity: number | null,
    totalRetailMediaSpend: number | null
): number | null => {
    if (
        !isDefinedNumber(expectedVelocity)
        || !isDefinedNumber(period.promotion_price)
        || !isDefinedNumber(period.cost_per_unit)
        || !isDefinedNumber(totalRetailMediaSpend)
    ) {
        return null;
    }

    return expectedVelocity
        * (period.promotion_price - period.cost_per_unit)
        - totalRetailMediaSpend;
};
const calculateTotalTradeSpend = (
    period: PlanByPeriodItem,
    expectedVelocity: number | null
): number | null => {
    if (
        !isDefinedNumber(period.base_price)
        || !isDefinedNumber(period.promotion_price)
        || !isDefinedNumber(expectedVelocity)
    ) {
        return null;
    }

    return (period.base_price - period.promotion_price) * expectedVelocity;
};

const calculatePeriodMetrics = (period: PlanByPeriodItem): PeriodMetrics => {
    const totalVelocityChange = calculateTotalVelocityChange(period);
    const expectedVelocity = calculateExpectedVelocity(period, totalVelocityChange);
    const totalRetailMediaSpend = calculateTotalRetailMediaSpend(period.plan_by_media_type);
    const totalTradeSpend = calculateTotalTradeSpend(period, expectedVelocity);
    const expectedRevenue = calculateExpectedRevenue(period, expectedVelocity);
    const expectedProfit = calculateExpectedProfit(
        period,
        expectedVelocity,
        totalRetailMediaSpend
    );
    return {
        totalVelocityChange,
        expectedVelocity,
        totalRetailMediaSpend,
        totalTradeSpend,
        expectedRevenue,
        expectedProfit
    };
};

const CommercialPlanGridSkeleton: React.FC = () => (
    <div className="border rounded-3 shadow-sm bg-white mt-1 overflow-hidden min-w-0 w-100">
        <div className="px-3 px-lg-4 py-3 border-bottom bg-light-subtle">
            <SovraSkeleton shape="text" style={{ width: "220px", height: 16 }} />
            <div className="mt-2">
                <SovraSkeleton shape="text" style={{ width: "340px", height: 28 }} />
            </div>
            <div className="mt-3 border-start border-3 border-primary ps-3">
                <SovraSkeleton shape="text" style={{ width: "75%" }} />
                <SovraSkeleton shape="text" style={{ width: "65%" }} />
            </div>
        </div>

        <div className="table-responsive commercial-plan-table-scroll">
            <table className="table align-middle mb-0">
                <thead className="table-light">
                    <tr className="commercial-plan-row">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <th key={index}>
                                <SovraSkeleton shape="text" style={{ width: index === 4 ? "140px" : "80px" }} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 3 }).map((_, retailerIndex) => (
                        <Fragment key={retailerIndex}>
                            <tr className="table-light">
                                <td colSpan={6} className="px-3 py-3">
                                    <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                                            <SovraSkeleton shape="text" style={{ width: "18px", height: 20 }} />
                                            <SovraSkeleton shape="text" style={{ width: "160px", height: 22 }} />
                                            <SovraSkeleton shape="text" style={{ width: "80px", height: 18 }} />
                                        </div>
                                        <div className="d-flex flex-wrap gap-3">
                                            {Array.from({ length: 4 }).map((_, metricIndex) => (
                                                <SovraSkeleton
                                                    key={metricIndex}
                                                    shape="text"
                                                    style={{ width: "120px", height: 20 }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            {Array.from({ length: 2 }).map((_, rowIndex) => (
                                <tr key={`${retailerIndex}-${rowIndex}`}>
                                    {Array.from({ length: 6 }).map((_, cellIndex) => (
                                        <td key={cellIndex}>
                                            <SovraSkeleton
                                                shape={cellIndex === 0 || cellIndex === 2 ? "rectangle" : "text"}
                                                style={{
                                                    width: cellIndex === 4 ? "180px" : "90px",
                                                    height: cellIndex === 4 ? 56 : 20,
                                                    borderRadius: 8
                                                }}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </Fragment>
                    ))}
                </tbody>
            </table>
        </div>

        <div className="px-3 px-lg-4 py-4 border-top bg-light-subtle">
            <div className="row g-3">
                {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="col-12 col-lg-4">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-body">
                                <SovraSkeleton shape="text" style={{ width: "160px", height: 24 }} />
                                <div className="mt-3 d-grid gap-2">
                                    <SovraSkeleton shape="text" />
                                    <SovraSkeleton shape="text" style={{ width: "85%" }} />
                                    <SovraSkeleton shape="text" style={{ width: "65%" }} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const CommercialPlanTable: React.FC<CommercialPlanTableProps> = ({ planByRetailer, handleActionButtonClick }) => {
    const [showDriverData, setShowDriverData] = useState(true);
    const toggleShowDriverData = useCallback(() => {
        setShowDriverData(!showDriverData);
    }, [showDriverData]);

    const userIsAdministrator = isAdministrator();

    return (
        <div className="commercial-plan-table-card border rounded-3 shadow-sm bg-white overflow-hidden min-w-0 w-100">
            <div className="px-3 px-lg-4 py-3 border-bottom">
                <div className="commercial-plan-table-header">
                    <div className="min-w-0">
                        <h2 className="fs-5 fw-semibold text-body mb-0">AI Generated Commercial Plan</h2>
                    </div>
                    {userIsAdministrator && (
                        <div className="commercial-plan-table-header-actions">
                            <Button
                                type="button"
                                size="small"
                                className={`px-3 gap-3 ${showDriverData ? "volume-driver-active" : "volume-driver-idle"}`}
                                onClick={toggleShowDriverData}
                            >
                                <div className="d-flex gap-2 align-items-center">
                                    {showDriverData ? <Eye className="w-3.5 h-3.5" size={12} /> : <EyeOff className="w-3.5 h-3.5" size={12} />}
                                    Show demand drivers
                                </div>
                            </Button>
                            <Button
                                type="button"
                                size="small"
                                themeColor="secondary"
                                fillMode="outline"
                                svgIcon={dataJsonIcon}
                                className="px-3"
                                onClick={handleActionButtonClick}
                            >
                                View JSON
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="table-responsive commercial-plan-table-scroll min-w-0">
                <table className="table table-sm align-middle mb-0 commercial-plan-table">
                    <thead className="table-light border-light-subtle">
                        <tr className="border-bottom">
                            <th scope="col" className="!k-text-subtle fs-8 fw-medium text-nowrap px-3 py-2">Week</th>
                            <th scope="col" className="!k-text-subtle fs-8 fw-medium text-nowrap px-3 py-2">SKU price</th>
                            <th scope="col" className="!k-text-subtle fs-8 fw-medium text-nowrap px-3 py-2">Promotion type</th>
                            <th scope="col" className="!k-text-subtle fs-8 fw-medium text-nowrap px-3 py-2">Retail media spend</th>
                            <th scope="col" className="!k-text-subtle fs-8 fw-medium text-nowrap px-3 py-2">Expected revenue</th>
                            <th scope="col" className="!k-text-subtle fs-8 fw-medium text-nowrap px-3 py-2">Expected profit</th>
                        </tr>
                    </thead>
                    <tbody className="small">
                        {planByRetailer.map(retailer => {
                            return (
                                <Fragment key={retailer.retailer_id}>
                                    {retailer.plan_by_period.map((row, i, arr) => {
                                        const metrics = calculatePeriodMetrics(row);
                                        const deltaStores = (arr[i--].number_stores ?? 0) - (row.number_stores ?? 0);

                                        return (
                                            <Fragment key={`${retailer.retailer_id}-${row.period_sequence_id}`}>
                                                <tr className="border-light-subtle">
                                                    <td className="px-6 py-3 text-nowrap">
                                                        <span
                                                            className="badge rounded-pill bg-body-tertiary border border-light-subtle text-secondary fw-semibold fs-8"
                                                            title={row.period_plan_rationale}
                                                        >
                                                            {`${row.period_sequence_id}`}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-3 text-nowrap !k-text-tertiary fw-medium">{formatCurrency(row.promotion_price)}</td>
                                                    <td className="px-3 py-3 text-nowrap">
                                                        {row.promotion === "None" || !row.promotion
                                                            ? <span className="text-secondary small">None</span>
                                                            : <span className="fw-semibold k-text-primary">{row.promotion}</span>}
                                                    </td>
                                                    <td className="px-3 py-3 text-nowrap fw-medium !k-text-tertiary">{formatCurrency(metrics.totalRetailMediaSpend)}</td>
                                                    <td className="px-3 py-3 text-nowrap fw-medium !k-text-tertiary">{formatCurrency(metrics.expectedRevenue)}</td>
                                                    <td className="px-3 py-3 text-nowrap fw-medium k-text-primary">{formatCurrency(metrics.expectedProfit)}</td>
                                                </tr>
                                                {showDriverData && metrics.totalVelocityChange != 0 && (
                                                    <tr className="commercial-plan-summary-row fs-8 fw-medium k-text-brand-teal">
                                                        <td colSpan={6} className="!k-bg-brand-sidebar-accent px-6">
                                                            {`↑ Velocity +${formatPercentage(metrics.totalVelocityChange)} ${deltaStores ? `| ${deltaStores} stores (promo distribution)` : ""}`}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const CommercialPlanGrid: React.FC<CommercialPlanGridProps> = ({ planData }) => {
    const [isJsonWindowVisible, setIsJsonWindowVisible] = useState<boolean>(false);
    const [formattedPlanJson, setFormattedPlanJson] = useState<string>();
    const [hasCopiedJson, setHasCopiedJson] = useState<boolean>(false);

    if (!planData) {
        return <CommercialPlanGridSkeleton />;
    }

    const handleViewJsonClick = (): void => {
        setFormattedPlanJson(JSON.stringify(planData, null, 2));
        setHasCopiedJson(false);
        setIsJsonWindowVisible(true);
    };

    const handleCopyJsonClick = (): void => {
        if (!formattedPlanJson || !navigator.clipboard) {
            return;
        }

        void navigator.clipboard.writeText(formattedPlanJson).then(() => {
            setHasCopiedJson(true);
        });
    };

    return (
        <div className="d-flex flex-column gap-4 min-w-0 w-100">
            <DataConfidence />

            <CommercialPlanTable planByRetailer={planData.plan_by_retailer} handleActionButtonClick={handleViewJsonClick} />

            <CommercialPlanInsights
                planReasoning={planData.plan_reasoning}
                caveats={planData.caveats}
                suggestions={planData.suggestions}
            />

            <DataQuality />

            {isJsonWindowVisible && formattedPlanJson && (
                <Window
                    title="Commercial Plan JSON"
                    width={900}
                    height={650}
                    minWidth={320}
                    minHeight={300}
                    modal
                    onClose={() => setIsJsonWindowVisible(false)}
                    style={{ maxWidth: "95vw", maxHeight: "85vh" }}
                >
                    <div className="d-flex justify-content-end mb-2">
                        <Button
                            type="button"
                            size="small"
                            themeColor="secondary"
                            fillMode="outline"
                            svgIcon={clipboardIcon}
                            className="px-3"
                            onClick={handleCopyJsonClick}
                        >
                            {hasCopiedJson ? "Copied" : "Copy JSON"}
                        </Button>
                    </div>
                    <pre
                        className="mb-0 bg-body-tertiary border rounded-3 p-3 small"
                        style={{
                            fontFamily: "var(--bs-font-monospace), SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace",
                            maxHeight: "calc(85vh - 7rem)",
                            overflow: "auto",
                            whiteSpace: "pre"
                        }}
                    >
                        {formattedPlanJson}
                    </pre>
                </Window>
            )}
        </div>
    );
};
export default CommercialPlanGrid;
